import { AttachFile, Close, CloudUpload, GppGood, LockOutlined } from "@mui/icons-material";
import { Box, Button, CircularProgress, createTheme, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select, TextField, ThemeProvider, Typography } from "@mui/material";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import logo from '../../images/logo.png';
import { ComplaintService } from "../../services/ComplaintService";
import { ComplaintDTO } from "../../types/ComplaintDTO";
import { complaintTypes } from "../complaint/Complaint";
import './ComplaintForm.scss';

export const lightTheme = createTheme({
	palette: {
		mode: 'light',
	},
});

const ComplaintForm: FC = () => {
	const [files, setFiles] = useState<File[]>([]);
	const [sectors, setSectors] = useState<string[]>([]);
	const [positions, setPositions] = useState<string[]>([]);
	const [loading, setLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);
	const [complaint, setComplaint] = useState<ComplaintDTO>({
		position: "Não desejo informar"
	} as ComplaintDTO);
	const service = new ComplaintService();
	const { id } = useParams<{ id: string }>();
	const { showError } = useAlert();
	const navigate = useNavigate();

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (event.target.files) {
			const newFiles = Array.from(event.target.files);
			setFiles(prevFiles => [...prevFiles, ...newFiles]);
		}
	};

	const handleChange = (field: keyof ComplaintDTO, value: string) => {
		setComplaint(prev => ({
			...prev,
			[field]: value
		}));
	};

	const removeFile = (fileToRemove: File) => {
		setFiles(prevFiles => prevFiles.filter(file => file !== fileToRemove));
	};

	const handleClickGoHome = () => {
		navigate(`/canal-de-denuncias/${id}/acompanhamento/`);
	};

	useEffect(() => {
		const loadSectors = async () => {
			setLoading(true);
			if (id) {
				try {
					const response = await service.getSectors(id);
					if (response && response.data) {
						setSectors(response.data);
					}
				} catch (error) {
					setNotFound(true);
				}
			} else {
				setNotFound(true);
			}
			setLoading(false);
		};
		loadSectors();
	}, [id]);

	useEffect(() => {
		const loadPositions= async () => {
			const response = await service.getPositions(id, complaint.sector || '');
			if (response && response.data) {
				setPositions(response.data);
			}
		}
		if (complaint.sector) {
			loadPositions();
		}
	}, [complaint.sector]);

	if (loading) {
		return (
			<ThemeProvider theme={lightTheme}>
				<div className="complaint-form-page">
					<CircularProgress />
				</div>
			</ThemeProvider>
		);
	}

	if (notFound) {
		return (
			<ThemeProvider theme={lightTheme}>
				<div className="complaint-form-page">
					<Box className="form-container">
						<img src={logo} alt="Logo da empresa" className="form-logo" onClick={handleClickGoHome} />
						<Typography variant="h5" align="center">
							Canal de denúncias não encontrado ou inativo
						</Typography>
					</Box>
				</div>
			</ThemeProvider>
		);
	}

	return (
		<ThemeProvider theme={lightTheme}>
			<div className="complaint-form-page">
				<Box className="form-container">
					<img src={logo} alt="Logo da empresa" className="form-logo" onClick={handleClickGoHome} />
					<header className="form-header">
						<GppGood />
						<Typography variant="h6">Canal seguro e confidencial - Sua identidade está protegida</Typography>
					</header>
					<main className="form-body">
						<FormControl fullWidth>
							<InputLabel id="sector-select-label">Setor</InputLabel>
							<Select
								labelId="sector-select-label"
								value={complaint.sector || ''}
								label="Setor"
								onChange={(e) => handleChange("sector", e.target.value)}
							>
								{sectors.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
							</Select>
						</FormControl>
						<FormControl fullWidth disabled={!complaint.sector}>
							<InputLabel id="role-select-label">Cargo</InputLabel>
							<Select
								labelId="role-select-label"
								value={complaint.position || 'Não desejo informar'}
								label="Cargo"
								onChange={(e) => handleChange("position", e.target.value)}
							>
								<MenuItem disabled value="">Selecione a opção que mais se aproxima</MenuItem>
								<MenuItem value="Não desejo informar">Não desejo informar</MenuItem>
								{positions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
							</Select>
							<FormHelperText>
								Você pode optar por não informar
							</FormHelperText>
						</FormControl>
						<FormControl fullWidth>
							<InputLabel id="type-select-label">Tipo de denúncia</InputLabel>
							<Select
								labelId="type-select-label"
								value={complaint.complaintType || ''}
								label="Tipo de denúncia"
								onChange={(e) => handleChange("complaintType", e.target.value)}
							>
								{complaintTypes.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
							</Select>
						</FormControl>
						<TextField
							label="Descrição da denúncia"
							multiline
							rows={6}
							value={complaint.description || ''}
							onChange={(e) => handleChange("description", e.target.value)}
							placeholder="Detalhe sua denúncia com o máximo de detalhes possível."
						/>
						<div className="upload-section">
							<Button variant="outlined" component="label" startIcon={<CloudUpload />}>
								Anexar Arquivos
								<input type="file" hidden multiple onChange={handleFileChange} />
							</Button>
							<div className="file-list">
								{files.map((file, index) => (
									<div key={index} className="file-item">
										<AttachFile fontSize="small" />
										<Typography variant="body2" className="file-name">{file.name}</Typography>
										<IconButton size="small" onClick={() => removeFile(file)}><Close fontSize="small" /></IconButton>
									</div>
								))}
							</div>
						</div>
						<Button
							onClick={save}
							variant="contained"
							color="primary"
							size="large"
							className="submit-button"
							disabled={!complaint.sector || !complaint.complaintType || !complaint.description || complaint.description.length < 20}
						>
							{loading
								? <CircularProgress color="error" size={24} />
								: "Enviar Denúncia"
							}
						</Button>
						<Button variant="contained" color="primary" onClick={handleClickGoHome}>
							Acompanhar denúncia existente
						</Button>
					</main>
					<footer className="form-footer">
						<LockOutlined />
						<Typography variant="body2">Todas as informações são tratadas com confidencialidade e em conformidade com a LGPD</Typography>
					</footer>
				</Box>
			</div>
		</ThemeProvider>
	);

	async function save() {
		setLoading(true);
		try {
			const payload = new FormData();
			payload.append("data", JSON.stringify(complaint));
			files.forEach((file) => {
				payload.append("files", file);
			});
			const response = await service.register(id, payload);
			if (response && response.data) {
				navigate(`/canal-de-denuncias/${id}/protocolo/${response.data.id}`);
			}
		} catch (error) {
			showError("Erro ao registrar denúncia! Tente novamente mais tarde");
		}
		setLoading(false);
	}
}

export default ComplaintForm;